import type { WidgetConfig } from "./contract";

export const EXCHANGE_PARAM = "_tinker_exchange";
export const CAPTURE_STORAGE_PREFIX = "tinker.capture:";

export type InstalledContext = {
  apiBase: string;
  installationId: string;
  observedBuildRef: string;
};

type StoredCapture = {
  captureToken: string;
  artifactId: string;
};

export type InstalledActivation =
  | { state: "active"; config: WidgetConfig; authorization: string }
  | { state: "dormant" | "rejected" | "unreachable" };

export function installedContext(
  tag: HTMLScriptElement | null,
): InstalledContext | null {
  const apiBase = (tag?.getAttribute("data-tinker-api") ?? "").replace(/\/+$/, "");
  const installationId = tag?.getAttribute("data-tinker-installation") ?? "";
  const observedBuildRef = tag?.getAttribute("data-tinker-build") ?? "";

  if (!apiBase || !installationId || !observedBuildRef) return null;
  return { apiBase, installationId, observedBuildRef };
}

export function captureStorageKey(apiBase: string, installationId: string): string {
  return `${CAPTURE_STORAGE_PREFIX}${apiBase}|${installationId}`;
}

export async function activateInstalled(
  context: InstalledContext,
  win: Window,
  doFetch: typeof fetch,
): Promise<InstalledActivation> {
  const key = captureStorageKey(context.apiBase, context.installationId);
  const pageUrl = new URL(win.location.href);
  const code = pageUrl.searchParams.get(EXCHANGE_PARAM);

  if (code) return exchange(context, win, doFetch, key, code);

  const stored = readStored(win, key);
  if (!stored) return { state: "dormant" };

  try {
    const response = await doFetch(`${context.apiBase}/i/config`, {
      credentials: "omit",
      cache: "no-store",
      headers: {
        authorization: `Bearer ${stored.captureToken}`,
        "x-tinker-observed-build": context.observedBuildRef,
      },
    });

    if (!response.ok) {
      if (await readableError(response)) {
        removeStored(win, key);
        return { state: "rejected" };
      }
      return { state: "unreachable" };
    }

    const config = (await response.json()) as WidgetConfig;
    if (!validInstalledConfig(config) || config.artifact_id !== stored.artifactId) {
      removeStored(win, key);
      return { state: "rejected" };
    }

    return {
      state: "active",
      config,
      authorization: `Bearer ${stored.captureToken}`,
    };
  } catch {
    return { state: "unreachable" };
  }
}

async function exchange(
  context: InstalledContext,
  win: Window,
  doFetch: typeof fetch,
  key: string,
  code: string,
): Promise<InstalledActivation> {
  const currentUrl = win.location.href;
  const form = new URLSearchParams({
    code,
    installation_id: context.installationId,
    current_url: currentUrl,
    observed_build_ref: context.observedBuildRef,
  });

  try {
    const response = await doFetch(`${context.apiBase}/i/exchange`, {
      method: "POST",
      credentials: "omit",
      cache: "no-store",
      headers: { "content-type": "application/x-www-form-urlencoded;charset=UTF-8" },
      body: form.toString(),
    });

    if (!response.ok) {
      if (await readableError(response)) {
        removeStored(win, key);
        return { state: "rejected" };
      }
      return { state: "unreachable" };
    }

    const body = (await response.json()) as {
      config?: WidgetConfig;
      capture_token?: string;
    };
    if (!validInstalledConfig(body.config) || !body.capture_token)
      return { state: "unreachable" };

    writeStored(win, key, {
      captureToken: body.capture_token,
      artifactId: body.config.artifact_id,
    });
    scrubExchange(win);

    return {
      state: "active",
      config: body.config,
      authorization: `Bearer ${body.capture_token}`,
    };
  } catch {
    return { state: "unreachable" };
  }
}

function readStored(win: Window, key: string): StoredCapture | null {
  try {
    const raw = win.sessionStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredCapture>;
    if (
      typeof value.captureToken !== "string" ||
      typeof value.artifactId !== "string"
    ) {
      removeStored(win, key);
      return null;
    }
    return { captureToken: value.captureToken, artifactId: value.artifactId };
  } catch {
    return null;
  }
}

function writeStored(win: Window, key: string, value: StoredCapture): void {
  try {
    win.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Capture can still run in this tab when storage is unavailable.
  }
}

function removeStored(win: Window, key: string): void {
  try {
    win.sessionStorage.removeItem(key);
  } catch {
    // Nothing else to clear.
  }
}

function scrubExchange(win: Window): void {
  const url = new URL(win.location.href);
  url.searchParams.delete(EXCHANGE_PARAM);
  win.history.replaceState(
    win.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

async function readableError(response: Response): Promise<boolean> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === "string";
  } catch {
    return false;
  }
}

function validInstalledConfig(value: WidgetConfig | undefined): value is WidgetConfig {
  return Boolean(
    value &&
    typeof value.artifact_id === "string" &&
    value.widget &&
    (value.build_status === "matching" || value.build_status === "drifted"),
  );
}
